// src/ui/CallsPanel.jsx — the call history, in the left column.
//
// 🟢 SOURCED FROM THE PHONE'S EXPORT, because Firestore alone cannot rebuild this list:
//    the per-call relay docs are store-and-forward (consumed and deleted once a phone
//    ingests them). The export already ships every `call_history` system message with
//    its full callData, so the web reads the same records the app's Calls tab reads.
import React, { useMemo, useEffect, useState, useCallback } from 'react';
import Avatar, { resolveName } from './Avatar';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Video, Plus, Timer } from './icons';
import CallLinkModal from './CallLinkModal';
import { timeLabel, dayLabel } from './format';
import { otherParticipant } from '../services/chats';
import { iBlockedNow, blockedMeNow, unblockUser } from '../services/blocking';   // 🚫
import { subscribeCalls, getCallState, startCall, pullCallHere } from '../services/calls';
import { startGroupCall, joinGroupCall, groupCallBlockedReason, getGroupCallState } from '../services/groupCalls';
import { subscribeCallFeed } from '../services/callFeed';
import {
  subscribeCallLogDeletions, hideCalls, clearAllCalls, isCallHidden,
  sessionKeyOf, EMPTY_LEDGER,
} from '../services/callLogSync';

function durLabel(sec) {
  const s = Number(sec) || 0;
  if (!s) return '';
  const m = Math.floor(s / 60);
  return m ? `${m}m ${s % 60}s` : `${s}s`;
}

export default function CallsPanel({ myUid, chats, syncedHistory }) {
  // 🟢 live call state — drives the same green / dim / alert logic the chat header has
  const [callState, setCallState] = useState(getCallState());
  useEffect(() => subscribeCalls(setCallState), []);
  // 🟢 live records from the server's call-history-sync broadcast — a call that just
  //    ended appears here immediately, without waiting for the next phone export
  const [feed, setFeed] = useState([]);
  useEffect(() => subscribeCallFeed(setFeed), []);

  /* ☎️ THE DELETION LEDGER — the only thing this browser and the phone share
     about the call log. Both keep their own local list (localStorage here,
     SQLite there); this is what makes a delete on either one stick on both.
     See services/callLogSync.js. */
  const [ledger, setLedger] = useState(EMPTY_LEDGER);
  useEffect(() => subscribeCallLogDeletions(myUid, setLedger), [myUid]);

  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null);   // 'selected' | 'all' | null
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState(false);  // the call-link manager

  const stopPicking = useCallback(() => { setPicking(false); setPicked(new Set()); }, []);
  const rows = useMemo(() => {
    const out = [];
    const seen = new Set();
    const byId = new Map((chats || []).map((c) => [c.id, c]));
    const nameFor = (chat, peer) => chat
      ? (chat.isGroup ? (chat.name || chat.groupName || 'Group') : resolveName(chat?.participantNames?.[peer] ? { fullName: chat.participantNames[peer] } : null) || chat?.participantNames?.[peer] || 'Allo user')
      : 'Allo user';

    /* 🟥 "Allo user" IS `nameFor` SAYING IT DOES NOT KNOW, not a name — so the
       recorded one has to win over it, and only over it. A first cut wrote
       `(chat ? nameFor(...) : '') || recorded`, which looks right and is wrong in
       exactly the case this exists for: a chat that exists but has no name for
       this person returns the "Allo user" placeholder, which is truthy, so the
       real name recorded at call time never got a look in. */
    const pickName = (chat, peer, recorded) => {
      const fromChat = chat ? nameFor(chat, peer) : '';
      if (fromChat && fromChat !== 'Allo user') return fromChat;
      return recorded || 'Allo user';
    };
    // live feed first — freshest data wins the dedup
    for (const r of feed || []) {
      const peer = (r.participants || []).find((p) => p && p !== myUid);
      /* 🟥 THE RECORD'S OWN chatId WINS OVER A DERIVED ONE. The derivation
         `[myUid, peer].sort().join('_')` is the shape of a ONE-TO-ONE chat id,
         so for a group call it named a conversation this call never happened in
         — usually one that does not exist, occasionally a real 1:1 with whoever
         `find()` happened to return first. Either way the row lost the group:
         no group name, and an avatar wearing one member's face.
         The id is on the wire (see callFeed.js); deriving it was only ever a
         workaround for normalising it away. */
      const chatId = r.chatId || (peer ? [myUid, peer].sort().join('_') : '');
      const chat = byId.get(chatId);
      /* 🟢 A GROUP HAS NO "the other person", and asking for one is what put a
         member's face on a group row. `isGroup` from the record, or the chat
         document itself once we have found it. */
      const isGroupCall = !!(r.isGroup || chat?.isGroup);
      if (!peer && !isGroupCall) continue;
      const outgoing = r.callInitiatedBy === myUid;
      const dir = outgoing ? 'outgoing' : r.outcome === 'answered' ? 'incoming' : 'missed';
      const key = String(r.id).replace(/^call_end_/, '');
      seen.add(key);
      /* 🟥 THE RECORDED NAME WINS OVER THE CHAT, and for a guest it is the only
         one there is. A guest CALL link creates no chat document at all — that is
         the design, not an omission — so `nameFor` had nothing to read and every
         guest call in this list said "Allo user". The chat is still the fallback
         for ordinary calls, where it carries the nickname this person set. */
      /* 🟢 …but a recorded name is one PERSON's name, so it is not a fallback
         for a group. A group whose chat document is not in my list (left, or not
         yet synced) reads as "Group" rather than as whichever member the record
         happened to list first. */
      const recorded = isGroupCall
        ? ''
        : ((r.participants || [])[0] === peer ? r.fromName : r.toName);
      const isGuest = !isGroupCall && !!r.guestUid && r.guestUid === peer;
      out.push({
        /* 🟥 `key` IS NOT `id`. The two sources name rows differently — the feed
           uses `feed_<session>` and the export uses the message id
           `call_end_<session>` — and the ledger is keyed on the bare session id
           alone. Carrying it explicitly is what stops a delete from writing one
           spelling and the filter reading another, which would look like the
           delete worked and then quietly undid itself. */
        key,
        id: `feed_${key}`, chatId, chat,
        isGroup: isGroupCall,
        name: isGroupCall ? (chat ? nameFor(chat, null) : 'Group') : pickName(chat, peer, recorded),
        isGuest,
        video: r.callType === 'video', dir,
        duration: r.outcome === 'answered' ? r.duration : 0, ts: r.ts,
      });
    }
    for (const [chatId, msgs] of Object.entries(syncedHistory || {})) {
      for (const m of msgs || []) {
        const cd = m.callData;
        if (!cd || (m.systemType !== 'call_history' && m.type !== 'system')) continue;
        if (!cd.callType) continue;
        // dedup against the live feed (both key on the call session id)
        const key = String(cd.callSessionId || m.id || '').replace(/^call_end_/, '');
        if (key && seen.has(key)) continue;
        const chat = byId.get(chatId);
        /* 🟢 the same rule as the live branch above: a group has no "other
           person", and `otherParticipant` on one returns an arbitrary member */
        const isGroupCall = !!chat?.isGroup;
        const peer = (chat && !isGroupCall) ? otherParticipant(chat, myUid) : null;
        const outgoing = cd.callInitiatedBy === myUid;
        // the app's derivation: initiator → outgoing; else answered → incoming, else missed
        const dir = outgoing ? 'outgoing' : (cd.answered || cd.outcome === 'answered') ? 'incoming' : 'missed';
        out.push({
          key,                                  // the same bare session id — see above
          id: m.id || `${chatId}_${m.timestamp}`,
          chatId,
          chat,
          isGroup: isGroupCall,
          /* the phone's export carries the same two fields the live feed does —
             see callHistorySaver.ts, which stores them on `callData` */
          name: isGroupCall ? nameFor(chat, null) : pickName(chat, peer, cd.peerName),
          isGuest: !isGroupCall && !!cd.guestUid && (!peer || cd.guestUid === peer),
          video: cd.callType === 'video',
          dir,
          duration: cd.duration,
          ts: typeof m.timestamp === 'number' ? m.timestamp : 0,
        });
      }
    }
    out.sort((a, b) => b.ts - a.ts);
    /* 🟢 filtered LAST, so the dedup above still sees a hidden row and a deleted
       call cannot be resurrected by its twin from the other source */
    return out.filter((r) => !isCallHidden(ledger, r.key || r.id, r.ts));
  }, [syncedHistory, chats, myUid, feed, ledger]);

  const toggle = (k) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const runDelete = async () => {
    setBusy(true);
    try {
      if (confirm === 'all') {
        await clearAllCalls(myUid);
      } else {
        await hideCalls(myUid, [...picked]);
      }
      stopPicking();
    } catch {
      /* 🟢 the ledger is a Firestore write and the SDK queues it offline, so the
         realistic failure here is a rules refusal, not a dropped connection. The
         list simply does not change — better than pretending it did. */
      window.alert('Could not delete those calls. Try again.');
    } finally { setBusy(false); setConfirm(null); }
  };

  const DirIcon = { outgoing: PhoneOutgoing, incoming: PhoneIncoming, missed: PhoneMissed };

  let lastDay = null;
  return (
    <div className="calls-panel">
      <div className="cp-head">
        {picking ? (
          <>
            <b>{picked.size} selected</b>
            <div className="cp-head-acts">
              <button className="lg-go slim ghost" onClick={stopPicking}>Cancel</button>
              <button
                className="lg-go slim danger"
                disabled={picked.size === 0 || busy}
                onClick={() => setConfirm('selected')}
              >
                Delete
              </button>
            </div>
          </>
        ) : (
          <>
            <b>Calls</b>
            {/* 🟥 THE + IS NOT INSIDE THE rows.length GUARD. An empty call log is
                exactly when somebody is most likely to want a link to a person
                who is not on Allo — hiding the only way to make one until you
                already have call history is backwards. */}
            <div className="cp-head-acts">
              {rows.length > 0 && (
                <>
                  <button className="lg-go slim ghost" onClick={() => setPicking(true)}>Select</button>
                  <button className="lg-go slim ghost" onClick={() => setConfirm('all')}>Clear log</button>
                </>
              )}
              <button
                className="cp-new"
                title="Call link for someone not on Allo"
                aria-label="Create a call link"
                onClick={() => setLinking(true)}
              >
                <Plus width={16} height={16} />
              </button>
            </div>
          </>
        )}
      </div>
      <div className="cp-list">
        {rows.length === 0 && (
          <div className="ct-empty"><Phone width={24} height={24} /><b>No calls yet</b>
            <span>Voice and video calls with your contacts will appear here.</span>
            <button className="lg-go slim ghost" onClick={() => setLinking(true)}>
              Call someone not on Allo
            </button>
          </div>
        )}
        {rows.map((r) => {
          const day = dayLabel(r.ts);
          const header = day !== lastDay ? <div className="ct-day" key={`d-${r.id}`}>{day}</div> : null;
          lastDay = day;
          const D = DirIcon[r.dir] || Phone;
          return (
            <React.Fragment key={r.id}>
              {header}
              <div
                className={`cp-row${picking ? ' picking' : ''}${picking && picked.has(r.key || r.id) ? ' picked' : ''}`}
                onClick={picking ? () => toggle(r.key || r.id) : undefined}
              >
                {picking && (
                  <input
                    type="checkbox"
                    className="cp-check"
                    checked={picked.has(r.key || r.id)}
                    readOnly
                    tabIndex={-1}
                  />
                )}
                {/* 🟥 A GROUP GETS NO peerUid. Avatar already knows how to draw a
                    group — its own photo, else a collage of member photos, else
                    the coloured initial — but only when it is not simultaneously
                    handed one member to draw instead. That is what put a single
                    member's face on every group call in this list. */}
                <Avatar
                  chat={r.chat}
                  peerUid={(r.chat && !r.isGroup) ? otherParticipant(r.chat, myUid) : null}
                  size="sm"
                  myUid={myUid}
                />
                <div className="cp-body">
                  <b className={r.dir === 'missed' ? 'missed' : ''}>{r.name}</b>
                  <span><D width={13} height={13} /> {r.dir === 'missed' ? 'Missed' : r.dir === 'outgoing' ? 'Outgoing' : 'Incoming'}
                    {r.duration ? ` · ${durLabel(r.duration)}` : ''} · {timeLabel(r.ts)}</span>
                </div>
                {/* 🟥 AND NONE AT ALL FOR A GUEST — not "once the link expires",
                    NEVER. A guest cannot be rung: a browser holding a call link
                    has no push token, no CallKit and nothing to wake it, which is
                    why the whole design has the guest dialling out. A call button
                    here would ring into a void that nobody is listening to, and
                    it would do it from a row whose account may not exist any
                    more. The chip says what this row was instead. */}
                {r.isGuest && !picking && (
                  <span className="cp-guest" title="A guest, through a call link">
                    <Timer width={12} height={12} /> Guest
                  </span>
                )}
                {/* 🟥 NO CALL BUTTONS WHILE SELECTING. The row itself is the
                    checkbox target, and a stray tap on the phone icon while
                    picking rows to delete would start a call — the single worst
                    possible misfire on this screen. */}
                {/* 👥 A GROUP ROW DIALS THE GROUP, gated on the group's own
                    "Start calls" permission — the same field the phone reads, so
                    an admin turning it off from either client greys these out on
                    both. A row whose group is no longer in my list has nothing to
                    dial, so it shows no buttons rather than buttons that fail. */}
                {/* 🟢 A GROUP WHOSE CALL IS STILL RUNNING offers Join instead of
                    dial — the same swap the chat header and the info pane make,
                    off the same `activeCall` field. */}
                {!picking && r.isGroup && r.chat?.activeCall?.participants?.length > 0 && (
                  <button
                    className="hdr-join"
                    onClick={(e) => { e.stopPropagation(); joinGroupCall(r.chat); }}
                    title="Join the group call"
                  >
                    {r.chat.activeCall.callType === 'voice'
                      ? <Phone width={14} height={14} />
                      : <Video width={14} height={14} />}
                    Join
                  </button>
                )}
                {!picking && r.isGroup && !(r.chat?.activeCall?.participants?.length > 0) && (() => {
                  const blocked = r.chat ? groupCallBlockedReason(r.chat, myUid) : 'This group is no longer in your list';
                  const press = (kind) => {
                    if (blocked) return;
                    if (callState.status !== 'idle' || getGroupCallState().status !== 'idle') return;
                    startGroupCall(r.chat, kind === 'video', null);
                  };
                  return (
                    <>
                      {/* 🟢 the SAME classes the 1:1 buttons beside them use —
                          `call-dim` is the existing "present but not available"
                          look, so a blocked group button reads exactly like a
                          blocked one-to-one button rather than like a new kind
                          of control nobody has seen before. */}
                      <button
                        className={`icon-btn${blocked ? ' call-dim' : ''}`}
                        disabled={!!blocked}
                        onClick={(e) => { e.stopPropagation(); press('voice'); }}
                        title={blocked || 'Voice call'}
                      ><Phone width={17} height={17} /></button>
                      <button
                        className={`icon-btn${blocked ? ' call-dim' : ''}`}
                        disabled={!!blocked}
                        onClick={(e) => { e.stopPropagation(); press('video'); }}
                        title={blocked || 'Video call'}
                      ><Video width={17} height={17} /></button>
                    </>
                  );
                })()}
                {!picking && !r.isGuest && !r.isGroup && (() => {
                  const peer = r.chat ? otherParticipant(r.chat, myUid) : null;
                  const busyHere = callState.status !== 'idle';
                  const withThis = busyHere && callState.peerUid === peer;
                  const elsewhereThis = !busyHere && callState.elsewhere?.peerUid === peer;
                  const liveKind = withThis ? callState.callType : elsewhereThis ? callState.elsewhere.callType : null;
                  const press = (kind) => {
                    if (!peer || r.chat?.isGroup) return;
                    if (withThis) return;
                    if (elsewhereThis && kind === liveKind) { pullCallHere(); return; }
                    if (busyHere || callState.elsewhere) { window.alert('You are in an active call. End it before starting another.'); return; }
                    /* 🚫 A BLOCK: the blocker is offered the door back, the blocked is told. */
                    if (iBlockedNow(peer)) {
                      if (!window.confirm(`You blocked ${r.name || 'this person'}. Unblock and call?`)) return;
                      unblockUser(myUid, peer).then(() => startCall(peer, kind === 'video')).catch(() => window.alert('Could not unblock. Please try again.'));
                      return;
                    }
                    if (blockedMeNow(peer)) { window.alert(`You can’t call ${r.name || 'this person'} right now.`); return; }
                    startCall(peer, kind === 'video');
                  };
                  const cls = (kind) => `icon-btn${liveKind === kind ? ' call-live' : liveKind ? ' call-dim' : ''}`;
                  return (
                    <>
                      <button className={cls('voice')} onClick={() => press('voice')} title={liveKind === 'voice' ? 'Ongoing voice call' : 'Voice call'}><Phone width={17} height={17} /></button>
                      <button className={cls('video')} onClick={() => press('video')} title={liveKind === 'video' ? 'Ongoing video call' : 'Video call'}><Video width={17} height={17} /></button>
                    </>
                  );
                })()}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {linking && <CallLinkModal myUid={myUid} onClose={() => setLinking(false)} />}

      {confirm && (
        <div className="vb-fixed-share" onClick={() => !busy && setConfirm(null)}>
          <div className="vb-sheet cp-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="vb-sheet-head">
              <b>{confirm === 'all' ? 'Clear the call log?' : `Delete ${picked.size} call${picked.size === 1 ? '' : 's'}?`}</b>
            </div>
            <div className="vb-sheet-body">
              <p className="cp-confirm-body">
                {confirm === 'all'
                  ? 'Every call disappears from this browser and from your phone. The people you called are not affected, and this cannot be undone.'
                  : 'These calls disappear from this browser and from your phone. The people you called are not affected, and this cannot be undone.'}
              </p>
            </div>
            <div className="vb-sheet-foot cp-confirm-foot">
              <button className="lg-go ghost" disabled={busy} onClick={() => setConfirm(null)}>Cancel</button>
              <button className="lg-go danger" disabled={busy} onClick={runDelete}>
                {busy ? 'Deleting…' : (confirm === 'all' ? 'Clear log' : 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
